import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Building2, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { ContactForm } from "@/components/ContactForm";

const DETAILS = [
  { icon: Building2, label: "Business name", value: "Strategic Racing", sub: "ABN 52 047 318 854", id: "business" },
  { icon: MapPin, label: "Address", value: "Unit 24806, Tower 2, 16 Casino Drive", sub: "Broadbeach Qld 4218", id: "address" },
  { icon: Phone, label: "Phone", value: "0431 121 020", href: "tel:0431121020", id: "phone" },
  { icon: Mail, label: "Email", value: "admin@strategic-racing.com.au", href: "mailto:admin@strategic-racing.com.au", id: "email" },
];

const PRIVACY = [
  "We only collect what we need to run your account: your name, email address and subscription status.",
  "Payments are processed securely by Stripe. We never see or store your card details.",
  "Your password is stored encrypted and cannot be read by anyone, including us.",
  "We use your email only for account, trial and subscription messages, plus support replies.",
];

const DATA = [
  "Your personal data is never sold or shared with any third party.",
  "It is stored on secure servers and used only to provide the Strategic Ratings service.",
  "You can ask for a copy of your data, or for your account and data to be deleted, at any time by emailing us.",
  "We handle personal information in line with the Australian Privacy Principles (Privacy Act 1988).",
];

const Detail = ({ icon: Icon, label, value, sub, href, id }) => (
  <div className="panel p-5 flex gap-4" data-testid={`contact-${id}`}>
    <Icon className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
    <div className="min-w-0">
      <div className="eyebrow mb-1">{label}</div>
      {href ? <a href={href} className="text-white font-semibold hover:text-emerald-300 break-all transition-colors">{value}</a>
        : <div className="text-white font-semibold">{value}</div>}
      {sub && <div className="text-sm text-slate-400">{sub}</div>}
    </div>
  </div>
);

const Points = ({ title, items, testId }) => (
  <div className="panel p-6" data-testid={testId}>
    <h3 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-400" />{title}</h3>
    <ul className="space-y-2.5 text-sm text-slate-300">
      {items.map((t) => <li key={t} className="flex gap-2"><span className="text-emerald-400">•</span><span>{t}</span></li>)}
    </ul>
  </div>
);

export default function Contact() {
  const { hash } = useLocation();
  useEffect(() => { if (hash) document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }); }, [hash]);
  return (
    <div className="min-h-screen bg-[#060A12] text-slate-200" data-testid="contact-page">
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-4 space-y-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors" data-testid="contact-home-link"><ArrowLeft className="h-4 w-4" />Strategic Ratings</Link>
        <section className="space-y-6">
          <div>
            <div className="eyebrow text-emerald-400 mb-2">Contact &amp; Support</div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="contact-title">We're here to help.</h1>
            <p className="text-slate-400 mt-3 max-w-xl">Questions about your subscription, ratings or account? Call or email and we'll get back to you as soon as possible.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">{DETAILS.map((d) => <Detail key={d.id} {...d} />)}</div>
          <ContactForm />
        </section>
        <section id="privacy" className="space-y-6 scroll-mt-6">
          <div>
            <div className="eyebrow text-emerald-400 mb-2">Privacy</div>
            <h2 className="font-display text-3xl font-bold text-white">Your privacy matters.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Points title="Privacy summary" items={PRIVACY} testId="contact-privacy-summary" />
            <Points title="How we handle your personal data" items={DATA} testId="contact-data-handling" />
          </div>
          <p className="text-sm text-emerald-300 font-semibold" data-testid="contact-never-shared">Your personal data is never sold or shared with a third party.</p>
        </section>
      </div>
      <SiteFooter />
    </div>
  );
}
